import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import IconView from "./IconView";

type _OutlineFloatingActionButtonOnPressed = () => void;

type _ButtonOnPressed = (d3eState: OutlineFloatingActionButtonRefs) => void;

export interface OutlineFloatingActionButtonProps extends BaseUIProps {
  key?: string;
  icon?: ui.IconData;
  iconColor?: ui.Color;
  iconSize?: number;
  disableMode?: boolean;
  buttonBorderColor?: ui.Color;
  onPressed?: _OutlineFloatingActionButtonOnPressed;
}
/// To store state data for OutlineFloatingActionButton
class OutlineFloatingActionButtonRefs {
  public button: ButtonState = new ButtonState();
}

interface ButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: OutlineFloatingActionButtonRefs;
  _onPressedButtonHandler?: _ButtonOnPressed;
  buttonBorderColor: ui.Color;
  disableMode: boolean;
  icon: ui.IconData;
  iconColor: ui.Color;
  iconSize: number;
}

class ButtonState extends ObjectObservable {}

class _ButtonWithState extends ObservableComponent<ButtonWithStateProps> {
  buttonFocusNode: ui.FocusNode = new ui.FocusNode();
  public constructor(props: ButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get button(): ButtonState {
    return this.props.d3eState.button;
  }
  public get buttonBorderColor(): ui.Color {
    return this.props.buttonBorderColor;
  }
  public get disableMode(): boolean {
    return this.props.disableMode;
  }
  public get icon(): ui.IconData {
    return this.props.icon;
  }
  public get iconColor(): ui.Color {
    return this.props.iconColor;
  }
  public get iconSize(): number {
    return this.props.iconSize;
  }
  public get d3eState(): OutlineFloatingActionButtonRefs {
    return this.props.d3eState;
  }
  public get _onPressedButtonHandler(): _ButtonOnPressed {
    return this.props._onPressedButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("button", null, this.button);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      [
        "button",
        "buttonBorderColor",
        "disableMode",
        "icon",
        "iconColor",
        "iconSize",
      ],
      this.rebuild
    );
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    return Button({
      disable: this.disableMode,
      decoration: new ui.BoxDecoration({
        border: ui.Border.all({
          color:
            this.buttonBorderColor !== null
              ? this.buttonBorderColor
              : ui.HexColor.fromHexInt(0xfd4600ff),
          width: 0.5,
        }),
        borderRadius: ui.BorderRadius.circular(30.0),
      }),
      onPressed: () => {
        this._onPressedButtonHandler(this.d3eState);
      },
      onFocusChange: (val) => {},
      child: IconView({
        icon: this.icon,
        color:
          this.iconColor !== null
            ? this.iconColor
            : ui.HexColor.fromHexInt(0xfd4600ff),
        size: this.iconSize !== 0.0 ? this.iconSize : 24.0,
        className: "x42a vc",
      }),
      className: ui.join(
        this.props.className,
        "OutlineFloatingActionButton vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
}
function ButtonWithState(props: ButtonWithStateProps) {
  return React.createElement(_ButtonWithState, props);
}

class _OutlineFloatingActionButtonState extends ObservableComponent<OutlineFloatingActionButtonProps> {
  static defaultProps = {
    icon: null,
    iconColor: null,
    iconSize: 0.0,
    disableMode: false,
    buttonBorderColor: null,
    onPressed: null,
  };
  d3eState: OutlineFloatingActionButtonRefs =
    new OutlineFloatingActionButtonRefs();
  public constructor(props: OutlineFloatingActionButtonProps) {
    super(props);

    this.initState();
  }
  public get icon(): ui.IconData {
    return this.props.icon;
  }
  public get iconColor(): ui.Color {
    return this.props.iconColor;
  }
  public get iconSize(): number {
    return this.props.iconSize;
  }
  public get disableMode(): boolean {
    return this.props.disableMode;
  }
  public get buttonBorderColor(): ui.Color {
    return this.props.buttonBorderColor;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["buttonBorderColor", "disableMode", "icon", "iconColor", "iconSize"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: OutlineFloatingActionButtonProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.icon !== this.props.icon) {
      this.fire("icon", this);
    }

    if (prevProps.iconColor !== this.props.iconColor) {
      this.fire("iconColor", this);
    }

    if (prevProps.iconSize !== this.props.iconSize) {
      this.fire("iconSize", this);
    }

    if (prevProps.disableMode !== this.props.disableMode) {
      this.fire("disableMode", this);
    }

    if (prevProps.buttonBorderColor !== this.props.buttonBorderColor) {
      this.fire("buttonBorderColor", this);
    }
  }
  public render(): ReactNode {
    return ButtonWithState({
      d3eState: this.d3eState,
      _onPressedButtonHandler: this.onPressedButtonHandler,
      buttonBorderColor: this.buttonBorderColor,
      disableMode: this.disableMode,
      icon: this.icon,
      iconColor: this.iconColor,
      iconSize: this.iconSize,
    });
  }
  public onPressedButtonHandler = (
    d3eState: OutlineFloatingActionButtonRefs
  ): void => {
    if (!this.disableMode && this.onPressed !== null) {
      this.onPressed();
    }
  };
  public get onPressed(): _OutlineFloatingActionButtonOnPressed {
    return this.props.onPressed;
  }
  public get button() {
    return this.d3eState.button;
  }
}
export default function OutlineFloatingActionButton(
  props: OutlineFloatingActionButtonProps
) {
  return React.createElement(_OutlineFloatingActionButtonState, {
    ..._OutlineFloatingActionButtonState.defaultProps,
    ...props,
  });
}
